import { useGetMarketCurrentPtcQuery } from "@apis/pricing/usePricing"
import { useGetMarketsByZipCodeQuery } from "@apis/offers/useOffers"
import { Skeleton } from "@components/ui/skeleton"
import { formatDate } from "@utils/formatters"

type UtilityInformationProps = {
  market_code: string
  revenue_class_code: string
  zip_code: string
}

export const UtilityInformation = ({
  market_code,
  revenue_class_code,
  zip_code,
}: UtilityInformationProps) => {
  const getMarketPtcQuery = useGetMarketCurrentPtcQuery(
    market_code,
    revenue_class_code,
  )

  const getMarketsByZipCodeQuery = useGetMarketsByZipCodeQuery(zip_code)

  const selectedMarket = getMarketsByZipCodeQuery?.data?.find(
    (market) => market.market_code === market_code,
  )

  if (getMarketPtcQuery.isLoading || getMarketsByZipCodeQuery.isLoading) {
    return (
      <div className="tw:pt-4">
        <Skeleton className="tw:h-4 tw:w-1/3" />
        <Skeleton className="tw:mt-2 tw:h-3 tw:w-2/3" />
        <Skeleton className="tw:mt-1 tw:h-3 tw:w-1/4" />
      </div>
    )
  }

  if (getMarketPtcQuery.isError || getMarketsByZipCodeQuery.isError) {
    return (
      <div className="tw:pt-4 tw:text-sm tw:text-red-500">
        Failed to load utility information.
      </div>
    )
  }

  const formatPtcData = () => {
    if (getMarketPtcQuery?.data?.rate === "NA") return "N/A"
    return `${getMarketPtcQuery?.data?.rate} ${getMarketPtcQuery?.data?.rate_currency} / ${getMarketPtcQuery?.data?.rate_units} as of ${formatDate(getMarketPtcQuery?.data?.from_dt)}`
  }

  const generateBillSampleLink = () => {
    return `${import.meta.env.VITE_CUST_SITE}/utilities/${market_code}/bill-samples/subscription`
  }

  return (
    <div className="tw:pt-4 tw:text-sm tw:text-muted-foreground">
      <h4 className="tw:font-semibold">
        {selectedMarket?.market_name} Information
      </h4>
      <hr />
      <div className="tw:flex tw:flex-col tw:gap-1">
        <>
          <strong>Account Identifier</strong>
          <p className="tw:capitalize">{selectedMarket?.account_num_synonym}</p>
        </>
        <>
          <strong>Price to Compare</strong>
          <p>{formatPtcData()}</p>
        </>
        <a
          href={generateBillSampleLink()}
          className="tw:text-sm tw:text-sky-700"
          target="_blank"
          rel="noopener noreferrer"
        >
          View Bill Sample
        </a>
      </div>
    </div>
  )
}
